import {CoworkBarePanel} from '../admin/CoworkFormPanel';
import {Portal as LumaPortal} from '@headlessui/react';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { fetchProductosCafeteria, ProductoCafeteria } from '../../services/productoCafeteriaService';
import './productosReserva.css';

export interface ProductoSeleccionado {
  producto: number;
  nombre: string;
  cantidad: number;
  precio_unitario: number;
  monto_descuento?: number;
}

export default function ProductosReservaSelector({ sucursalId, value, onChange, disabled = false }: {
  sucursalId: number; value: ProductoSeleccionado[];
  onChange: (items: ProductoSeleccionado[]) => void; disabled?: boolean;
}) {
  const [catalogo, setCatalogo] = useState<ProductoCafeteria[]>([]);
  const [busqueda, setBusqueda] = useState('');
  const [categoria, setCategoria] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(true);
  const [revision, setRevision] = useState(0);
  const [abierto, setAbierto] = useState(false);
  const panel = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    let vigente = true;
    setCargando(true); setError(''); setCatalogo([]);
    fetchProductosCafeteria({ sucursal_id: sucursalId, activo: true }).then(items => {
      if (vigente) setCatalogo(items.filter(p => p.activo && p.sucursal === sucursalId));
    }).catch(() => { if (vigente) setError('No se pudieron cargar los productos.'); })
      .finally(() => { if (vigente) setCargando(false); });
    return () => { vigente = false; };
  }, [sucursalId, revision]);
  useEffect(() => {
    if (!abierto) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panel.current?.querySelector<HTMLInputElement>('input')?.focus();
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAbierto(false);
      if (e.key !== 'Tab') return;
      const nodes = Array.from(panel.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), [tabindex="0"]') || []);
      const first = nodes[0], last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus(); }
      if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', key);
    return () => { document.body.style.overflow = previous; document.removeEventListener('keydown', key); trigger.current?.focus(); };
  }, [abierto]);
  const cambiar = (p: ProductoCafeteria, cantidad: number) => {
    if (disabled || !Number.isInteger(cantidad) || cantidad < 0 || cantidad > 999999) return;
    if (p.controlar_stock && cantidad > (p.stock ?? 0)) return;
    const anterior = value.find(v => v.producto === p.id);
    onChange([...value.filter(v => v.producto !== p.id), ...(cantidad ? [{
      producto: p.id, nombre: anterior?.nombre ?? p.nombre, cantidad,
      precio_unitario: anterior?.precio_unitario ?? Number(p.precio),
      monto_descuento: Math.min(anterior?.monto_descuento ?? 0, cantidad * (anterior?.precio_unitario ?? Number(p.precio))),
    }] : [])]);
  };
  const cambiarDescuento = (id: number, raw: string) => {
    if (disabled) return;
    const amount = Number(raw);
    if (!Number.isFinite(amount)) return;
    onChange(value.map(p => p.producto === id ? { ...p,
      monto_descuento: Math.round(Math.max(0, Math.min(amount, p.cantidad * p.precio_unitario)) * 100) / 100,
    } : p));
  };
  const total = value.reduce((s, p) => s + p.cantidad * p.precio_unitario - (p.monto_descuento || 0), 0);
  const unidades = value.reduce((s, p) => s + p.cantidad, 0);
  const categorias = Array.from(new Map(catalogo.filter(p => p.categoria).map(p => [p.categoria, p.categoria_info?.nombre || 'Otros'])).entries());
  const filtrados = catalogo.filter(p => (!categoria || p.categoria === categoria) && p.nombre.toLocaleLowerCase().includes(busqueda.toLocaleLowerCase()));
  return <section className="pr-summary" aria-label="Productos de cafetería">
    <div className="pr-summary-heading"><span className="pr-coffee" aria-hidden="true">☕</span><div><span className="pr-eyebrow">COMPLETA TU RESERVA</span><h3>Cafetería</h3></div><span className="pr-count">{unidades}</span></div>
    {!value.length && <p className="pr-muted">Un café, algo para compartir…<br/>Elige el complemento para tu espacio.</p>}
    {value.length > 0 && <ul className="pr-selection">{value.map(p => <li key={p.producto} style={{flexWrap:'wrap'}}>
      <span className="pr-quantity">{p.cantidad}×</span><span className="pr-item-name">{p.nombre}</span>
      <strong>Bs {(p.cantidad * p.precio_unitario - (p.monto_descuento || 0)).toFixed(2)}</strong>
      <button type="button" disabled={disabled} aria-label={`Eliminar ${p.nombre}`} onClick={() => onChange(value.filter(v => v.producto !== p.producto))}>×</button>
      <label style={{width:'100%',fontSize:12}}>Descuento de línea (Bs.)
        <input type="number" min="0" max={p.cantidad * p.precio_unitario} step="0.01" disabled={disabled}
          aria-label={`Descuento de línea de ${p.nombre}`} value={p.monto_descuento || ''}
          onChange={e => cambiarDescuento(p.producto, e.target.value)}
          style={{width:90,marginLeft:8,padding:5,border:'1px solid #cbd5e1',borderRadius:6}} />
      </label>
    </li>)}</ul>}
    <button type="button" ref={trigger} className="pr-open" disabled={disabled} onClick={() => setAbierto(true)}><span>＋</span>{value.length ? 'Editar productos' : 'Explorar cafetería'}<span>→</span></button>
    {value.length > 0 && <p className="pr-summary-total">Productos: Bs {total.toFixed(2)}</p>}
    {abierto && <LumaPortal>{<div className="pr-overlay" onMouseDown={e => { if (e.target === e.currentTarget) setAbierto(false); }}>
      <CoworkBarePanel className="pr-dialog" ref={panel} role="dialog" aria-modal="true" aria-label="Catálogo de cafetería">
        <header className="pr-header"><div><span className="pr-eyebrow">TU RESERVA, CON ALGO MÁS</span><h2>¿Qué te servimos?</h2><p>Selecciona tus favoritos. Se suman al total de tu reserva.</p></div><button type="button" className="pr-close" aria-label="Cerrar cafetería" onClick={() => setAbierto(false)}>×</button></header>
        <div className="pr-filters"><input aria-label="Buscar productos de cafetería" placeholder="Buscar café, jawitas, sándwiches…" value={busqueda} onChange={e => setBusqueda(e.target.value)}/>
          <div className="pr-categories"><button type="button" className={!categoria ? 'is-active' : ''} onClick={() => setCategoria(null)}>Todos</button>{categorias.map(([id, nombre]) => <button type="button" key={id} className={categoria === id ? 'is-active' : ''} onClick={() => setCategoria(id)}>{nombre}</button>)}</div>
        </div>
        <div className="pr-catalog">
          {cargando && <p role="status" className="pr-empty">Preparando el menú…</p>}
          {error && <p role="alert" className="pr-empty">{error} <button type="button" onClick={() => setRevision(v => v + 1)}>Reintentar</button></p>}
          {!cargando && !error && !filtrados.length && <p className="pr-empty">No encontramos productos. Prueba otra búsqueda o categoría.</p>}
          <div className="pr-grid">{filtrados.map(p => {
            const elegido = value.find(v => v.producto === p.id), cantidad = elegido?.cantidad ?? 0;
            const agotado = p.controlar_stock && (p.stock ?? 0) <= cantidad;
            return <article key={p.id} className={`pr-card ${cantidad ? 'is-selected' : ''}`}>
              <div className="pr-photo">{p.imagen ? <img src={p.imagen} alt={p.nombre} loading="lazy" onError={e => { e.currentTarget.style.display = 'none'; }}/> : null}<span aria-hidden="true">☕</span>{cantidad > 0 && <b className="pr-badge">{cantidad} en tu reserva</b>}</div>
              <div className="pr-card-body"><small>{p.categoria_info?.nombre || 'CAFETERÍA'}</small><h3>{p.nombre}</h3>{p.descripcion && <p className="pr-description">{p.descripcion}</p>}<div className="pr-price"><strong>Bs {(elegido?.precio_unitario ?? Number(p.precio)).toFixed(2)}</strong><span>{p.controlar_stock ? `${p.stock ?? 0} disponibles` : 'Por unidad'}</span></div>
                <div className="pr-stepper"><button type="button" disabled={disabled || !cantidad} aria-label={`Quitar una unidad de ${p.nombre}`} onClick={() => cambiar(p, cantidad - 1)}>−</button><input type="number" min="0" max={p.controlar_stock ? (p.stock ?? 0) : 999999} step="1" aria-label={`Cantidad de ${p.nombre}`} disabled={disabled} value={cantidad} onChange={e => cambiar(p, Number(e.target.value))}/><button type="button" disabled={disabled || agotado} aria-label={`Agregar una unidad de ${p.nombre}`} onClick={() => cambiar(p, cantidad + 1)}>+</button></div>
              </div>
            </article>;
          })}</div>
        </div>
        <footer className="pr-footer"><div><span>{unidades} {unidades === 1 ? 'producto seleccionado' : 'productos seleccionados'}</span><strong>Bs {total.toFixed(2)}</strong></div><button type="button" className="pr-done" onClick={() => setAbierto(false)}>Listo, volver a mi reserva <span>→</span></button></footer>
      </CoworkBarePanel>
    </div>}</LumaPortal>}
  </section>;
}

